// Copyright (c) 2021 WiseTime. All rights reserved.

import { combineEpics } from "redux-observable"
import { concat, EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { equals, flatten, uniq } from "ramda"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as TagsActions from "../actions"
import * as TagsSlice from "../slice"
import { BatchGetTagsResponse, BatchGetTagsRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import {
  getTagCategoriesIdsFromTimeline,
  getTagCategoriesIdsFromTimeRowsOrTimeGroups,
  getTagIdsFromTimeline,
  getTagIdsFromTimeRowsOrTimeGroups,
} from "../tags"
import * as UserContextSlice from "../../user-context/slice"
import * as UserContextActions from "../../user-context/actions"
import { t } from "@lingui/macro"
import { Code, executor, isConnectError, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"
import type { RootState } from "../../../app/store"
import { showSnackBar } from "../../snackbar/actions"
import { throttleSame } from "../../../lib/rxutils"

type BatchGetTagsFromTimelinesRpc = (idsList: string[]) => Observable<BatchGetTagsResponse>

export const batchGetTagsFromTimelines = (batchGetTagsRpc: BatchGetTagsFromTimelinesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineSlice.setTimeline.match),
      throttleSame(2000, ({ payload: a }, { payload: b }) => {
        const aTagIds = uniq(flatten(a.map(getTagIdsFromTimeline)))
        const bTagIds = uniq(flatten(b.map(getTagIdsFromTimeline)))
        return aTagIds.length === bTagIds.length && equals([...aTagIds].sort(), [...bTagIds].sort())
      }),
      mergeMap(action =>
        of(action.payload)
          .pipe(
            mergeMap(timelines => {
              const tagIdsList = uniq(flatten(timelines.map(getTagIdsFromTimeline)))
              if (tagIdsList.length === 0) {
                return EMPTY
              }
              return batchGetTagsRpc(tagIdsList)
            }),
            map(t => t.tags),
            mergeMap(tags => {
              const actions: Action[] = [TagsSlice.setTags(tags)]
              const tagCategoryIdsListFromTimelines = uniq(flatten(action.payload.map(getTagCategoriesIdsFromTimeline)))
              const tagCategoryIdsList: string[] = uniq(
                tags
                  .map(t => t.tagCategoryId)
                  .filter(id => !tagCategoryIdsListFromTimelines.includes(id) && id !== "")
              )
              if (tagCategoryIdsList.length) {
                actions.push(TagsActions.batchGetTagCategories(tagCategoryIdsList))
              }
              return concat(actions)
            }),
            catchError(handleError),
          )
      ),
    )

type BatchGetTagsFromTimeRowsRpc = (idsList: string[]) => Observable<BatchGetTagsResponse>

const idsListFromSetTimeRowsAction = (action: Action): string[] => {
  if (TimelineSlice.setTimeRowsForDates.match(action)) {
    return getTagIdsFromTimeRowsOrTimeGroups(action.payload.timeRows)
  }
  return []
}

export const batchGetTagsFromTimeRows = (batchGetTagsRpc: BatchGetTagsFromTimeRowsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineSlice.setTimeRowsForDates.match),
      mergeMap(action => of(idsListFromSetTimeRowsAction(action))
        .pipe(
          mergeMap(batchGetTagsRpc),
          map(t => t.tags),
          mergeMap(tags => {
            const actions: Action[] = [TagsSlice.setTags(tags)]
            if (TimelineSlice.setTimeRowsForDates.match(action)) {
              const tagCategoryIdsListFromTimeRows = getTagCategoriesIdsFromTimeRowsOrTimeGroups(action.payload.timeRows)
              const tagCategoryIdsList: string[] = uniq(
                tags
                  .map(t => t.tagCategoryId)
                  .filter(id => !tagCategoryIdsListFromTimeRows.includes(id) && id !== "")
              )
              if (tagCategoryIdsList.length !== 0) {
                actions.push(TagsActions.batchGetTagCategories(tagCategoryIdsList))
              }
            }
            return concat(actions)
          }),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.PermissionDenied) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_batch_get_tags_failed", message: "Failed to fetch tag details. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const batchGetTagsRpc = (idsList: string[]): Observable<BatchGetTagsResponse> =>
  executor(() => tagService.batchGetTags(create(BatchGetTagsRequestSchema, { tagIds: idsList })))

export default combineEpics<Action, Action, RootState, unknown>(
  batchGetTagsFromTimelines(batchGetTagsRpc),
  batchGetTagsFromTimeRows(batchGetTagsRpc),
)
